import React, { useState } from 'react';
import { 
  Palette, 
  Image, 
  Type, 
  Layers, 
  Download, 
  Save, 
  Plus,
  Eye,
  Grid,
  Zap,
  Sparkles,
  Wand2,
  Upload,
  MousePointer,
  Square,
  Circle,
  Triangle,
  Move
} from 'lucide-react';

export default function CreativeBuilder() {
  const [selectedTool, setSelectedTool] = useState('select');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [canvasElements, setCanvasElements] = useState([]);
  const [activeTab, setActiveTab] = useState('templates');

  const tools = [
    { id: 'select', name: 'Select', icon: MousePointer },
    { id: 'text', name: 'Text', icon: Type },
    { id: 'rectangle', name: 'Rectangle', icon: Square },
    { id: 'circle', name: 'Circle', icon: Circle },
    { id: 'triangle', name: 'Triangle', icon: Triangle },
    { id: 'image', name: 'Image', icon: Image },
    { id: 'move', name: 'Move', icon: Move }
  ];

  const templates = [
    {
      id: 1,
      name: 'Instagram Post',
      size: '1080x1080',
      category: 'Social Media',
      thumbnail: '/api/placeholder/300/300',
      premium: false
    },
    {
      id: 2,
      name: 'Instagram Story',
      size: '1080x1920',
      category: 'Social Media',
      thumbnail: '/api/placeholder/300/500',
      premium: true
    },
    {
      id: 3,
      name: 'Facebook Ad',
      size: '1200x628',
      category: 'Ads',
      thumbnail: '/api/placeholder/400/200',
      premium: false
    },
    {
      id: 4,
      name: 'Google Display Ad',
      size: '728x90',
      category: 'Ads',
      thumbnail: '/api/placeholder/400/150',
      premium: true
    },
    {
      id: 5,
      name: 'YouTube Thumbnail',
      size: '1280x720',
      category: 'Video',
      thumbnail: '/api/placeholder/400/225',
      premium: false
    },
    {
      id: 6,
      name: 'LinkedIn Banner',
      size: '1584x396',
      category: 'Social Media',
      thumbnail: '/api/placeholder/400/100',
      premium: true
    }
  ];

  const aiSuggestions = [
    "Add more contrast to your text",
    "Try a warmer color palette",
    "Consider adding a call-to-action button",
    "Your design looks great! Try experimenting with typography",
  ];

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg">
              <Palette className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-semibold text-gray-800">Creative Builder</h1>
          </div>
          <div className="text-sm text-gray-500">
            AI-Powered Design Studio
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">
            <Eye className="w-4 h-4" />
            Preview
          </button>
          <button className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">
            <Save className="w-4 h-4" />
            Save
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Tools & Properties */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200">
            {[
              { id: 'templates', name: 'Templates', icon: Grid },
              { id: 'elements', name: 'Elements', icon: Layers },
              { id: 'ai', name: 'AI Assistant', icon: Sparkles }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'templates' && (
              <div className="p-4">
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-800 mb-3">Choose a Template</h3>
                  <div className="space-y-3">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        className={`relative p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedTemplate === template.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedTemplate(template.id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gray-100 rounded border flex items-center justify-center">
                            <Image className="w-6 h-6 text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-gray-800 truncate">
                              {template.name}
                            </h4>
                            <p className="text-xs text-gray-500">{template.size}</p>
                            <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                              {template.category}
                            </span>
                          </div>
                          {template.premium && (
                            <div className="absolute top-2 right-2">
                              <Sparkles className="w-3 h-3 text-yellow-500" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'elements' && (
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-800 mb-3">Design Tools</h3>
                <div className="grid grid-cols-2 gap-2 mb-6">
                  {tools.map((tool) => (
                    <button
                      key={tool.id}
                      onClick={() => setSelectedTool(tool.id)}
                      className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-all ${
                        selectedTool === tool.id
                          ? 'border-blue-500 bg-blue-50 text-blue-600'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      <tool.icon className="w-5 h-5" />
                      <span className="text-xs font-medium">{tool.name}</span>
                    </button>
                  ))}
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-800 mb-2">Quick Add</h4>
                    <div className="space-y-2">
                      <button className="w-full flex items-center gap-3 p-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors">
                        <Upload className="w-4 h-4" />
                        Upload Image
                      </button>
                      <button className="w-full flex items-center gap-3 p-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors">
                        <Type className="w-4 h-4" />
                        Add Text
                      </button>
                      <button className="w-full flex items-center gap-3 p-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors">
                        <Square className="w-4 h-4" />
                        Add Shape
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'ai' && (
              <div className="p-4">
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-800 mb-3">AI Design Assistant</h3>
                  <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Wand2 className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-purple-800">AI Suggestions</span>
                    </div>
                    <div className="space-y-2">
                      {aiSuggestions.map((suggestion, index) => (
                        <div key={index} className="text-xs text-purple-700 bg-white bg-opacity-60 p-2 rounded">
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <button className="w-full flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all">
                    <Zap className="w-4 h-4" />
                    Generate AI Design
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Canvas Area */}
        <div className="flex-1 flex flex-col bg-gray-100">
          {/* Canvas Toolbar */}
          <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Zoom:</span>
                <select className="border border-gray-300 rounded px-2 py-1 text-sm">
                  <option>50%</option>
                  <option>75%</option>
                  <option selected>100%</option>
                  <option>125%</option>
                  <option>150%</option>
                  <option>200%</option>
                </select>
              </div>
              
              <div className="w-px h-6 bg-gray-300"></div>
              
              <div className="flex items-center gap-2">
                <button className="p-1 text-gray-600 hover:text-gray-800 transition-colors">
                  <Grid className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600">Show Grid</span>
              </div>
            </div>
            
            <div className="text-sm text-gray-600">
              Canvas: 1080 x 1080 px
            </div>
          </div>

          {/* Canvas */}
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="relative bg-white rounded-lg shadow-lg" style={{ width: '500px', height: '500px' }}>
              {/* Canvas Content */}
              <div className="absolute inset-4 border-2 border-dashed border-gray-300 rounded flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <Image className="w-12 h-12 mx-auto mb-2" />
                  <p className="text-sm">Start designing your creative</p>
                  <p className="text-xs mt-1">Choose a template or add elements</p>
                </div>
              </div>
              
              {/* Grid overlay when grid is enabled */}
              <div className="absolute inset-0 pointer-events-none opacity-20">
                <svg width="100%" height="100%" className="overflow-visible">
                  <defs>
                    <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                      <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e5e7eb" strokeWidth="1"/>
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Right Sidebar - Properties */}
        <div className="w-64 bg-white border-l border-gray-200">
          <div className="p-4">
            <h3 className="text-sm font-medium text-gray-800 mb-4">Properties</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Background Color
                </label>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-white border border-gray-300 rounded cursor-pointer"></div>
                  <input 
                    type="text" 
                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Canvas Size
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input 
                    type="number" 
                    className="px-2 py-1 text-xs border border-gray-300 rounded"
                    placeholder="Width"
                    defaultValue="1080"
                  />
                  <input 
                    type="number" 
                    className="px-2 py-1 text-xs border border-gray-300 rounded"
                    placeholder="Height"
                    defaultValue="1080"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Opacity
                </label>
                <input 
                  type="range" 
                  className="w-full"
                  min="0"
                  max="100"
                  defaultValue="100"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Status Bar */}
      <div className="bg-white border-t border-gray-200 px-4 py-2 flex items-center justify-between text-xs text-gray-600">
        <div className="flex items-center gap-4">
          <span>Elements: {canvasElements.length}</span>
          <span>•</span>
          <span>Last saved: Never</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <span>All changes saved</span>
        </div>
      </div>
    </div>
  );
} 